// Shared types
import { PageSlugs } from "../add-element/types";
import { PreferredContactMethod } from "@minted/addressbook-components";
import { Descendant } from "slate";

export type TimestampedModel = {
  id: number;
  createdAt: string;
  updatedAt: string;
  // In order to do Object.keys and Object.values, we have to
  // extend object explicitly for all of our models
} & object;

export type SortableFields = {
  position: number;
};

export type HasImageAndPhoto = {
  id: number;
  // represents cropped image on all models except Attendants
  image?: string | null;
  // represents foreign key to photo table, is a string on Attendants and
  // represents the cropped image on that model
  photo: number | null;
};

export type GeneralContentFields = {
  title: string | null;
  content: RichTextType;
} & HasImageAndPhoto;

export type SectionMedia = {
  resourceName: string;
  resource: {
    data: (Photo | Video) & { position: number };
  };
} & (Photo | Video) &
  GalleryMedia;

export type AddressFields = {
  venue: string | null;
  hasPhysicalAddress: boolean;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  country: string | null;
};

// This represents all editable resources, so sections, accommodations,
// attendants, etc. That's why many fields are optional
export type EditableResourceFields = {
  // Title is the primary freetext field for most resources, but it's
  // the formal title of the Attendant and the Name is primary
  title: string;
  // Accommodations
  description?: RichTextType | null;
  url?: string;
  // Attendants
  bio?: RichTextType;
  name?: string;
  // This is only on here for attendants, which uses 'photo' where it should use
  // 'image' so we had to name this photoPk
  photoPk?: number | null;

  // Event fields, used for display only, the event form is its own component
  date?: string;
  endDate?: string;
  time?: string;
  endTime?: string;
  timezone?: string | null;

  // Gallery sections
  sectionMedia?: SectionMedia[];
  sectionType?: string;
  galleryMedia?: GalleryMedia[];
  galleryMode?: string;

  // Custom sections
  content?: RichTextType | null;
  // Exists on the resource models
  position: number;
} & HasImageAndPhoto &
  Partial<AddressFields>;

export type Configuration = {
  imageAlignment: string;
  imageSize: number;
  pageLayout: string;
  showMap: boolean;
  showPageDividers?: boolean;
  textAlignment: string;
  rsvpSubheading?: string;
};

export type RichTextType = Descendant[];

// Demo types

export type DemoBasis = {
  id: string; // UUID field, override of TimestampedModel
  data: Record<string, string>;
  title: string;
  slug: string;
  kinds: string[];
  schema: Record<string, string>;
  siteSchema: Record<string, string>;
} & TimestampedModel;

export type DemoSite = {
  basis: string; // DemoBasis ID
  product: string; // Product ID
  data: Record<string, string>;
} & TimestampedModel;

// Site types

export type SeenUpsell = {
  code: string;
  date: string;
};

export enum TestValues {
  Control = "A",
  Experiment = "B",
  Variant = "C",
  Disabled = "OFF",
}

export const SiteFeatureFlag = {
  ConnectHouseholdMembers: "connectHouseholdMembers",
  NewLayouts: "newLayouts",
  oiDashboardV2: "oiDashboardV2",
  SectionEditorExperience: "sectionEditorExperience",
  StripeRegistry: "stripeRegistry",
} as const;

export type SiteFeatureFlagValue =
  (typeof SiteFeatureFlag)[keyof typeof SiteFeatureFlag];

export type FeatureFlags = Partial<Record<SiteFeatureFlagValue, TestValues>>;

export type WeddingWebsiteSiteDetails = {
  // Announcement (in settings page)
  showAnnouncement: boolean;
  announcementBody: string;
  announcementHeading: string;
  // Wedding date visibility (also in settings page)
  isWeddingDateVisible: boolean;
  // Your Names fields
  yourName: string;
  nameSeparator: string;
  spouseName: string;
  // The Big Day fields
  eventDateHeading: string;
  weddingDate: string | null;
  isCountdownVisible: boolean;
  countdownText: string;
  // Welcome Message fields
  isOurStoryVisible: boolean;
  welcomeHeading: string;
  welcomeText: string;
  // Absent pre-new-layouts (renders from layoutId instead); seeded at
  // creation once bucketed, so read behind isNewLayoutsEnabled(site).
  headerDisplay?: NamesDisplay | null;
  navigationStyle?: NavigationStyle | null;
  mediaDisplay?: MediaDisplay | null;
  // Empty means the default order for the site's layoutId
  headerElementOrder?: HeaderElement[];
  // Not directly user controlled
  seenUpsells: SeenUpsell[];
  lastNewContactsBannerDismissedDate: string | null;
};

export type OnlineInvitationSiteDetails = {
  commissionEligibilityDate: string | null;
  eventDescription: string | null;
  eventHeading: string;
  emailGreeting?: string | null;
  textGreeting?: string | null;
};

export type OnlineInvitationGuestCounts = {
  noReplyCount: number;
  numberAttending: number;
  numberMaybe: number;
  numberNotAttending: number;
  numberOfInvitesNotSent: number;
  numberOfInvitesSent: number;
  totalGuests: number;
};

export enum LayoutId {
  L1 = "l1",
  L2 = "l2",
  L3 = "l3",
  L4 = "l4",
}

/**
 * How the couple's names are displayed in the site header. Stored as
 * siteDetails.headerDisplay. Names axis only — independent of NavigationStyle
 * and MediaDisplay.
 */
export enum NamesDisplay {
  Horizontal = "horizontal",
  Large = "large",
  Medium = "medium",
  NamesOnly = "names_only",
  None = "none",
}

/** Which navigation bar treatment the site header uses. */
export enum NavigationStyle {
  Original = "original",
  Alternate = "alternate",
}

/** How the couple's photos are displayed in the site header. */
export enum MediaDisplay {
  Landscape = "landscape",
  FullBleed = "full_bleed",
  SideBySide = "side_by_side",
  Marquee = "marquee",
  Overlapping = "overlapping",
  Square = "square",
  Slideshow = "slideshow",
  None = "none",
}

/** The site header elements a host can reorder in the Layout editor. */
export enum HeaderElement {
  Navigation = "navigation",
  Names = "names",
  Media = "media",
}

/**
 * A photo or video in a site's modular header, in display order. Unlike
 * GalleryMedia, every key is always emitted, null-filled where it doesn't apply.
 */
export type HeaderMedia = {
  id: number; // WebsiteHeaderMedia's own pk — what header-media/sort/ expects
  mediaType: "photo" | "video";
  photo: string | null; // Photo uuid; null for video rows
  video: string | null; // Video uuid; null for photo rows
  original: string; // Absolute URL (crop-aware for photos; video preview thumbnail for videos)
  preview: string; // Absolute URL (crop-aware for photos; video preview thumbnail for videos)
  /** Raw storage path for a photo's crop override — not a browser URL. Prefer preview/original for display. */
  image: string | null;
  /** Cloudinary public id — video rows only, null for photo rows. */
  publicId: string | null;
  /** Video duration — video rows only, null for photo rows. */
  duration: string | null;
  position: number;
} & TimestampedModel;

type ProductDataDetails = {
  designName: string;
  kindDescription: string;
  url: string;
};

export type SiteNotificationSettings = {
  emailOnNewComments: boolean;
  emailOnNewRsvps: boolean;
};

/**
 * Stripe connected-account readiness, computed server-side from the
 * account's current requirements (webhook-driven, live-accurate).
 */
export type StripeFlags = {
  /** The account can accept gifts — show the registry, allow checkout. */
  giftsAllowed: boolean;
  /** The host can withdraw funds. Independent of `giftsAllowed`. */
  payoutsAllowed: boolean;
  /** Stripe needs something from the host right now (vs. merely reviewing). */
  awaitingHostAction: boolean;
};

/** Fallback for a site with no registry host yet, or a stale cached payload. */
export const DEFAULT_STRIPE_FLAGS: StripeFlags = {
  giftsAllowed: false,
  payoutsAllowed: false,
  awaitingHostAction: true,
};

export type Site = {
  customColorTheme: number | null;
  customColorThemes: CustomColorTheme[];
  deletedAt: string | null;
  status: string;
  firstEditedAt: string;
  domain: string;
  domainExpiration: string;
  domainHasExpired: boolean;
  domainIsCustom: boolean;
  domainIsExpiring: boolean;
  domainIsProvisioning: boolean;
  isDemoSite?: boolean;
  isTenant?: boolean;
  source: string;
  hasCustomDomainFeature: boolean;
  hasVisitedDesignsTab?: boolean;
  assets: Record<string, string>;
  headerMedia: HeaderMedia[];
  extraCss: string;
  layoutId: LayoutId;
  themeId: number;
  mintedProductSku: string;
  mintedProductColorChoiceId: string;
  themeDataOverrides: Record<string, string>;
  pageConfiguration: string;
  previewUrl?: string;
  siteType: string;
  featureFlags: FeatureFlags;
  siteDetails: WeddingWebsiteSiteDetails | OnlineInvitationSiteDetails;
  accessKey: string;
  password: string | null;
  isOnlineInvitation: boolean;
  isWedding: boolean;
  hasRsvpEvents: boolean;
  domainIsDefault: boolean;
  searchEngineVisibility: boolean;
  mintedSearchVisibility: boolean;
  indexingRequestVisible: boolean;
  indexingRequestAvailable: boolean;
  onlineInvitationGuestCounts: OnlineInvitationGuestCounts | null;
  sentInvitesCount: number;
  receivedRsvpCount: number;
  notificationSettings: SiteNotificationSettings | null;
  productData: ProductDataDetails;
  stripeFlags?: StripeFlags;
  hasStripeAccount?: boolean;
} & TimestampedModel;

// Outlining these differences lets typescript know what siteDetails is
// if you preface it with a site.isWedding check in scope.
export type WeddingSite = Site & {
  isWedding: true;
  isOnlineInvitation: false;
  siteDetails: WeddingWebsiteSiteDetails;
};

export type OnlineInvitationSite = Site & {
  isWedding: false;
  isOnlineInvitation: true;
  onlineInvitationFormattedEventDate: string;
  onlineInvitationRawEventDate: string | null;
  onlineInvitationGuestCounts: OnlineInvitationGuestCounts;
  siteDetails: OnlineInvitationSiteDetails;
};

export function isWeddingSite(site: Site): site is WeddingSite {
  return site.isWedding === true;
}

export function isOnlineInvitationSite(
  site: Site,
): site is OnlineInvitationSite {
  return site.isOnlineInvitation === true;
}

// Pages and general section types

export type Page = {
  site: number; // Page ID

  isDefault: boolean;
  isVisible: boolean;
  defaultName: string;
  displayName: string;
  givenName: string;
  slug: PageSlugs;
  configuration: Configuration;
  sections: Section[];
} & TimestampedModel &
  SortableFields &
  GeneralContentFields;

export type Photo = {
  original: string;
  preview: string;
  versions: Record<string, string>;
  id: string; // same as UUID, backend does mapping
  uuid: string;
  caption: string;
  pk: number; // Photo.id represents its UUID, but pk is the actual id
} & TimestampedModel;

export type Video = {
  id: string;
  pk: string;
  publicId: string;
  caption: string;
  duration: string;
  preview: string; // thumbnail
  uploadedAt: string;
} & TimestampedModel;

export const MediaTypes = {
  photo: "photo",
  video: "video",
};

/** A photo in the user's media library. Ordering belongs to the placement, so no `position`. */
export type LibraryPhoto = {
  caption: string | null;
  createdAt: string;
  mediaType: "photo";
  original: string;
  /** Photo's integer DB pk — what the API expects as `photo`. */
  pk: number;
  preview: string;
  /** Photo.uuid — the key `state.resources.photos.byId` is indexed by. */
  uuid: string;
};

/** A video in the user's media library. Its uuid pk doubles as its id. */
export type LibraryVideo = {
  caption: string | null;
  createdAt: string;
  duration: string;
  mediaType: "video";
  /** Video's uuid pk — never coerce this with Number() or parseInt(). */
  pk: string;
  preview: string; // thumbnail
  publicId: string;
};

/** A library item, discriminated on `mediaType` to keep the int/uuid pk spaces apart. */
export type LibraryMedia = LibraryPhoto | LibraryVideo;

/**
 * A library item from the picker. `editedImage` is set only when cropped this
 * session, and should be used directly rather than re-resolving from Redux.
 */
export type SelectedLibraryMedia = LibraryMedia & { editedImage?: string };

/**
 * A photo or video placed in a gallery section. The join row's id is never
 * serialized, so a placement is addressed by (section, library pk) — which is
 * why the same photo can't appear twice in one gallery.
 */
export type GalleryMedia = {
  caption: string | null;
  /** The JOIN row's timestamp — when the media was placed, not uploaded. */
  createdAt: string;
  duration?: string; // video specific attribute
  /** The library item's uuid. */
  id: string | number;
  /** Per-placement crop URL; absent or null when showing the library original. */
  image?: string | null;
  mediaType: "photo" | "video";
  original?: string;
  /** The library pk, stringified by the serializer for both media types. */
  pk: string;
  /** Order within this gallery. */
  position: number;
  preview: string;
  publicId?: string; // video specific attribute
  updatedAt?: string;
  /** The library row's timestamp — when the media was uploaded. */
  uploadedAt?: string;
};

// Generalized section and section details models

export enum SectionTypes {
  // These section types are "primary" types for specific pages
  Registry = "registry",
  Accommodation = "accommodation",
  Attendant = "attendant",
  // Event is not actually a section type, but we mix the event models
  // so that the events page can use the new system
  Event__FAKE = "event",
  // These are shared across all page types
  Text = "text",
  ImageAndText = "image_and_text",
  Gallery = "gallery",
  Media = "media",
  Divider = "divider",
  Button = "button",
  Anchor = "anchor",
  CashRegistry = "cash_registry",
  Location = "location",
  FAQ = "faq",
  Comments = "comments",
  // NOT AN ACTUAL SECTION TYPE, but used to visually group registries
  RegistryGroup = "registry_group",
  StripeRegistry = "stripe_registry",
}

export enum DisplayMode {
  Bottom = "bottom",
  Left = "left",
  Right = "right",
  Top = "top",
}

export type TextDetails = {
  title: string | null;
  content: RichTextType | null;
};

export type ImageAndTextDetails = {
  displayMode: DisplayMode;
} & TextDetails;

export type GalleryDetails = {
  title: string | null;
  content: RichTextType | null;
  galleryMode: string; // Replace with enum
};

export type MediaDetails = Record<string, never>;

export type DividerDetails = {
  dividerType: string; // Replace with enum
};

// WW-4679
// necessary?
export type CommentsDetails = {
  commentType: string;
};

export type ButtonDetails = {
  websiteUrl: string;
  buttonText: string | null;
};

export type AnchorDetails = {
  websiteUrl: string;
  anchorText: string | null;
};

export type AccommodationDetails = {
  title: string;
  content: RichTextType | null;
  websiteUrl: string | null;
  anchorText: string | null;
};

export type RegistryDetails = {
  title: string;
  content: RichTextType | null;
  websiteUrl: string | null;
  buttonText: string | null;
  hideDefaultRegistryImage?: boolean;
};

export type CashRegistryDetails = RegistryDetails & {
  // Embedded experience doesn't allow custom button text
  buttonText: null;
  cashRegistryAccessToken: string;
  cashRegistryId: string;
  username?: string;
  provider?: string;
  content?: string | null;
  textDescription?: string | null;
};

export type AttendantDetails = {
  content: RichTextType | null;
  displayMode: DisplayMode;
  name: string;
  title: string;
};

export type LocationDetails = {
  title: string;
  content: RichTextType | null;
} & AddressFields;

export type FAQDetails = {
  question: string;
  answer: RichTextType;
};

export type StripeRegistryDetails = {
  title: string;
  textDescription?: string | null;
  isFixedAmount: boolean;
  goalAmountCents: number;
  hideGoal: boolean;
  giftAmountCents: number;
  isVisible: boolean;
  showQuickOptions?: boolean;
  contributionTotalCents?: number;
};

export type ContributionItem = {
  type: "contribution";
  id: number;
  giverName: string;
  giverEmail: string;
  note: string;
  giftAmountCents: number;
  surchargeCents: number;
  applicationFeeCents: number;
  totalChargedCents: number;
  createdAt: string;
  sectionTitle: string;
};

export type PayoutItem = {
  type: "payout";
  id: number;
  amountCents: number;
  status: "pending" | "in_transit" | "paid" | "failed" | "canceled";
  createdAt: string;
};

export type TransactionItem = ContributionItem | PayoutItem;

export type SectionDetailsType =
  | TextDetails
  | ImageAndTextDetails
  | GalleryDetails
  | MediaDetails
  | DividerDetails
  | ButtonDetails
  | AnchorDetails
  | AccommodationDetails
  | RegistryDetails
  | CashRegistryDetails
  | StripeRegistryDetails
  | AttendantDetails
  | LocationDetails
  | FAQDetails
  | CommentsDetails;

export type Section = {
  id: number;
  page: number; // Page ID
  // legacy fields title and content should not be used in new system
  title: string;
  content: RichTextType | null;
  sectionDetails: SectionDetailsType;
  sectionType: SectionTypes;
  galleryMode: string; // Gallery section setting, if section type is gallery
  galleryMedia: GalleryMedia[];
  position: number;
  createdAt: string;
  updatedAt: string;
  registries?: RegistryDetails[];
  // Only Media and ImageAndText sections use this; mutually exclusive with photo
  video: string | null;
} & TimestampedModel &
  SortableFields &
  HasImageAndPhoto;

// Event types

export type Question = {
  event: number;
  text: string;
  questionType: string;
  required: boolean;
  choices?: string[];
} & TimestampedModel;

export type Answer = {
  rsvp: number;
  question: number;
  text: string;
};

// Named WebsiteEvent to avoid overlap with the existing js Event
export type WebsiteEvent = {
  site: number; // Site ID

  title: string;
  date: string;
  time: string;
  endDate: string;
  endTime: string;
  timezone: string | null;

  calendarUrls: Record<string, string>;

  // Not a model field
  formattedAddress: string;
  description: RichTextType;
  rsvpEnabled: boolean | null;
  private: boolean;
  caption: string;
  questions: Question[];

  // Online Invitation fields
  hostedBy: string;
  openInvitation: boolean;
  adultChildRsvpsEnabled: boolean;
  allowMaybeRsvp: boolean;
  // Guest list visibility is currently only an OI feature, but adding
  // it to site_details instead added considerable complexity
  showAttending: boolean;
  showMaybe: boolean;
  showNotAttending: boolean;
  showNotReplied: boolean;

  eventType: string;
} & TimestampedModel &
  SortableFields &
  HasImageAndPhoto &
  AddressFields;

export type Guest = {
  adultCount: number | null;
  addressbookContactId: string;
  addressbookMemberId: string;
  childCount: number | null;
  contactEmail: string;
  firstName: string;
  lastName: string;
  maxParty: number;
  party: number;
  rsvps: RSVP[];
  invites: Invite[];
} & TimestampedModel &
  SortableFields;

export type Invite = {
  event: number;
  guest: number;
  inviteSent: boolean;
} & TimestampedModel;

export type RSVP = {
  event: Pick<WebsiteEvent, "id">;
  guest: Guest;
  source: string;
  /** WW: use attending. OI: use rsvpStatus only. */
  attending: boolean | null;
  /** OI: "yes" | "no" | "maybe". WW: do not use. */
  rsvpStatus?: string;
  noteToHost: string | null;
  answers: Answer[];
} & TimestampedModel;

/** RSVP row keyed by event id on party guests (guest management); not the same as list API `Guest.rsvps`. */
export type GuestRsvpMapRow = {
  answers: Array<{ question: number; text: string }>;
  attending: boolean | null;
  id: number;
  rsvpStatus?: string;
};

export type GuestRsvpsByEventId = Record<number, GuestRsvpMapRow>;

/** Guest as embedded in party rows after `buildPartyDataByContactId` (rsvps is a per-event map). */
export type PartyGuest = Omit<Guest, "rsvps"> & {
  rsvps: GuestRsvpsByEventId;
};

export type MessageSendBatch = {
  body: string;
  createdAt: string;
  id: number;
  recipientCount: number;
  subject: string;
  toDescription: string;
};

export enum MessageSendStatus {
  Sending = "sending",
  Sent = "sent",
  Bounced = "bounced",
  Opened = "opened",
}

export enum MessageChannel {
  Email = "email",
  Text = "text",
}

// Add more only when needed, best not to clutter
export enum MessageType {
  HostContact = "host_contact",
  OnlineInvitation = "online_invitation",
}

export enum EmailTemplateNames {
  OiInvitation = "minted_oi_invitation",
}

export type MessageSendAttempt = {
  channel: MessageChannel;
  emailAddress: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  externalId: string | null;
  guest: number | null;
  messageType: MessageType;
  phoneNumber: string | null;
  site: number | null;
  status: MessageSendStatus;
  templateName: EmailTemplateNames | null;
  templateParams: Record<string, unknown> | null;
} & TimestampedModel;

export type RSVPContact = {
  id: string | null;
  name: string;
};

// EventSerializer as used by the RSVP form endpoints adds a computed
// `guests` list (guest ids invited to that event) not present elsewhere.
export type RSVPEvent = WebsiteEvent & {
  guests: number[];
};

// This is the response to selecting a name to RSVP
export type RSVPData = {
  contact: RSVPContact;
  events: RSVPEvent[];
  guests: Guest[];
  hasExistingRsvps?: boolean;
};

// Party data used in guest management (combines guest info with addressbook contact data)
export type PartyData = {
  address1?: string;
  address2?: string;
  addressbookContactId: string;
  city?: string;
  contactCreatedAt?: string;
  contactName: string;
  country?: string;
  email?: string;
  guests: PartyGuest[];
  messageSendStatus?: MessageSendStatus;
  partySize?: number;
  phoneNumber?: string;
  preferredContactMethod?: PreferredContactMethod;
  primaryMemberId?: string;
  sortKey: string;
  state?: string;
  zipCode?: string;
};

// Wedding resource types

export type Attendant = {
  site: number; // Site ID

  title: string;
  name: string;
  bio: RichTextType; // maps to description
  photo: string | null;
  // Attendants uses 'photo' where everything else uses 'image' so we had to
  // name this one photoPk instead. This is just the 'photo' field on every
  // other model and will be removed when consolidated
  photoPk: number | null;
} & TimestampedModel &
  SortableFields;

export type Accommodation = {
  site: number; // Site ID

  title: string;
  url: string;
  description: RichTextType;
} & TimestampedModel &
  SortableFields &
  HasImageAndPhoto;

export type Registry = {
  site: number; // Site ID

  title: string;
  url: string;
  description: RichTextType;

  identifier: string;
  cashRegistryAccessToken: string;
  cashRegistryId: string;
} & TimestampedModel &
  SortableFields &
  HasImageAndPhoto;

// Comment types

export type Comment = {
  site: number; // Site ID

  authorName: string;
  authorEmail: string;
  authorVisitorId: string;
  comment: string;
  parentComment: number; // Parent comment ID
  emailAuthorOnReplies: boolean;
  isSubscribed: boolean;
  replies?: Comment[];
} & TimestampedModel;

// Image Types

export type ImageUpload = {
  uuid: string;
  height: number;
  width: number;
} & TimestampedModel;

// 3rd Party

export type IncomingCashRegistryCreationRequest = {
  public_token: string;
  slug: string;
  id: string;
  identifier: string;
  title: string;
  description: string;
  recipient: string;
  recipient_last_name: string;
  recipient_2_first_name: string;
  recipient_2_last_name: string;
  date: string;
  total_gifters: number;
};

export type IncomingCashRegistryUpdateRequest = {
  cashRegistryId: string;
  description: RichTextType;
  slug: string;
  title: string;
};

// Colorway and dekura theme typing

export type RawColorway = {
  code: string;
  color: string;
  external_id: string;
  id: string;
  title: string;
};

export type TypographySettings = {
  fontFamily: string;
  fontName: string;
  fontSizeFactor: number;
  fontStyle: string;
  fontVariant: string;
  fontWeight: number;
  letterSpacing: string;
  lineHeightFactor: number;
  textTransform: string;
};

export type DekuraHeaderTypographyGroup = {
  CoupleHorizontalNames?: TypographySettings;
  CoupleVerticalNames?: TypographySettings;
  CoupleVerticalNamesSeparator?: TypographySettings;
  EventDate?: TypographySettings;
  EventDateHeading?: TypographySettings;
  EventSmallHeadline?: TypographySettings;
  IntroMediumHeadline?: TypographySettings;
  IntroText?: TypographySettings;
  NavbarTop?: TypographySettings;
};

export type DekuraRequiredTypographyGroup = {
  Button?: TypographySettings;
  LargeHeadline?: TypographySettings;
  MediumHeadline?: TypographySettings;
  NavbarTop?: TypographySettings;
  Text?: TypographySettings;
  TinyHeadline?: TypographySettings;
};

export type DekuraTypographySettings = {
  required: DekuraRequiredTypographyGroup;
  header: DekuraHeaderTypographyGroup;
};

export type PartialTypographySettings = Partial<TypographySettings>;

export type PartialDekuraTypographySettings = {
  required?: Partial<DekuraRequiredTypographyGroup>;
  header?: Partial<DekuraHeaderTypographyGroup>;
};

export type DekuraColorwayHeader = {
  backgroundColor: string;
  backgroundPatternPosition: string;
  backgroundPatternRepeat: string;
  fontColor: string;
  linkActiveColor: string;
  linkColor: string;
};

export type DekuraColorwayHeaderGroup = {
  InlineNavbar?: DekuraColorwayHeader;
  TopNavbar?: DekuraColorwayHeader;
  IntroMediumHeadline?: DekuraColorwayHeader;
  IntroContainer?: DekuraColorwayHeader;
};

export type DekuraArtwork = {
  LargeDivider: string;
  SmallDivider: string;
  SmallGraphic: string;
};

export type DekuraColors = {
  Button: string;
  LargeHeadline: string;
  MediumHeadline: string;
  SmallHeadline: string;
  TinyHeadline: string;
  backgroundColor: string;
  backgroundPatternPosition: string;
  backgroundPatternRepeat: string;
  fontColor: string;
  linkColor: string;
};

export type DekuraRequiredColorwayGroup = {
  artwork: DekuraArtwork;
  colors: DekuraColors;
};

export type DekuraColorway = {
  colorway: string; // Corresponds to colorwayCodeById
  header: DekuraColorwayHeaderGroup;
  required: DekuraRequiredColorwayGroup;
};

export type DekuraData = {
  colorways: DekuraColorway[];
  typography: DekuraTypographySettings;
};

export type Theme = {
  attribution: "string";
  colorwayCodeById: Record<number, string>;
  colorways: RawColorway[];
  dekuraData: DekuraData;
  fancyClothesData: any;
  title: string;
};

export enum ThemeColorCategory {
  background = "background",
  text = "text",
  secondaryBackground = "secondaryBackground",
  secondaryText = "secondaryText",
  linkAndButtonText = "linkAndButtonText",
}

export type ThemeColors = {
  [ThemeColorCategory.background]: string;
  [ThemeColorCategory.text]: string;
  [ThemeColorCategory.secondaryBackground]: string | null;
  [ThemeColorCategory.secondaryText]: string | null;
  [ThemeColorCategory.linkAndButtonText]: string;
};

// Customer defined color theme
export type CustomColorTheme = {
  id: number;
  createdAt: string;
  updatedAt: string;
} & ThemeColors;
